import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { professionalApi, professionalAuth } from "../lib/professionalApi";

/**
 * ProfessionalShell — app enxuto do COLABORADOR (login pessoal do
 * profissional). Isolado do CRM do dono: só a própria agenda e o próprio
 * faturamento, via /professional/* (escopado pelo token). Sem menu lateral.
 *
 * Fluxo: bootstrap por /professional/me → se must_change_password, tela de
 * troca obrigatória (bloqueante) → senão, abas "Minha Agenda" / "Meu
 * Faturamento".
 */

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const todayISO = () => new Date().toISOString().slice(0, 10);
const monthKey = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const fmtDayLabel = (iso) => {
    const [y, m, d] = String(iso || "").split("-").map(Number);
    if (!y) return iso;
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" }).replace(".", "");
};

const STATUS = {
    "agendado": { label: "Agendado", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
    "awaiting_confirmation": { label: "Aguarda", cls: "bg-amber-500/15 text-amber-200 border-amber-500/30" },
    "em-andamento": { label: "Em andamento", cls: "bg-sky-500/15 text-sky-200 border-sky-500/30" },
    "concluido": { label: "Concluído", cls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30" },
    "cancelado": { label: "Cancelado", cls: "bg-red-500/15 text-red-200 border-red-500/30" },
    "cancelled": { label: "Cancelado", cls: "bg-red-500/15 text-red-200 border-red-500/30" },
};

// ─── Troca de senha obrigatória (1º login) ─────────────────────────────
const ChangePasswordGate = ({ onDone }) => {
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [show, setShow] = useState(false);
    const [saving, setSaving] = useState(false);

    const valid = pw.length >= 8 && pw === pw2;

    const submit = async (e) => {
        e.preventDefault();
        if (!valid) return;
        setSaving(true);
        try {
            await professionalApi.changePassword(pw);
            toast.success("Senha alterada!");
            onDone();
        } catch (err) {
            toast.error("Não foi possível alterar", { description: err?.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center px-5" data-testid="professional-change-password-gate">
            <form onSubmit={submit} className="w-full max-w-sm space-y-4">
                <div className="text-center mb-2">
                    <div className="h-12 w-12 mx-auto rounded-2xl bg-amber-500/10 border border-amber-500/25 text-amber-300 flex items-center justify-center mb-3">
                        <i className="fa-solid fa-key text-lg"></i>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Primeiro acesso</div>
                    <h1 className="salon-heading text-2xl text-white mt-1">Crie sua senha</h1>
                    <p className="text-xs text-slate-500 mt-1">Você entrou com uma senha temporária. Defina uma senha pessoal para continuar.</p>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Nova senha</div>
                    <div className="relative">
                        <input
                            data-testid="professional-new-password"
                            type={show ? "text" : "password"}
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            placeholder="mín. 8 caracteres"
                            className="w-full bg-slate-900/60 border border-white/10 rounded-xl pl-4 pr-11 py-3 text-sm outline-none focus:border-amber-500/40"
                        />
                        <button type="button" onClick={() => setShow((v) => !v)}
                            className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-slate-500 hover:text-amber-200">
                            <i className={`fa-solid ${show ? "fa-eye-slash" : "fa-eye"} text-sm`}></i>
                        </button>
                    </div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Confirmar senha</div>
                    <input
                        data-testid="professional-new-password-confirm"
                        type={show ? "text" : "password"}
                        value={pw2}
                        onChange={(e) => setPw2(e.target.value)}
                        placeholder="repita a senha"
                        className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-500/40"
                    />
                    {pw && pw2 && pw !== pw2 && <div className="text-xs text-red-400 mt-1">As senhas não conferem.</div>}
                </div>
                <button
                    data-testid="professional-change-password-submit"
                    type="submit"
                    disabled={!valid || saving}
                    className="w-full min-h-[48px] rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold uppercase tracking-[0.15em] text-sm transition-all disabled:opacity-40"
                >
                    {saving ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Salvando…</> : "Salvar e continuar"}
                </button>
            </form>
        </div>
    );
};

// ─── Minha Agenda ─────────────────────────────────────────────────────
const AgendaTab = () => {
    const [date, setDate] = useState(todayISO());
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async (d) => {
        setLoading(true);
        try {
            const r = await professionalApi.agenda(d);
            setItems(r?.items || []);
        } catch (e) {
            toast.error("Falha ao carregar a agenda", { description: e?.message });
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(date); }, [date, load]);

    const shiftDay = (delta) => {
        const [y, m, d] = date.split("-").map(Number);
        const dt = new Date(y, m - 1, d + delta);
        setDate(dt.toISOString().slice(0, 10));
    };

    const setStatus = async (appt, status) => {
        setBusyId(appt.id);
        try {
            await professionalApi.setStatus(appt.id, status);
            setItems((arr) => arr.map((a) => (a.id === appt.id ? { ...a, status } : a)));
            toast.success(status === "em-andamento" ? "Atendimento iniciado" : "Atendimento concluído");
        } catch (e) {
            toast.error("Não foi possível atualizar", { description: e?.message });
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="space-y-4" data-testid="professional-agenda-tab">
            <div className="flex items-center justify-between gap-2">
                <button onClick={() => shiftDay(-1)} className="h-10 w-10 rounded-xl border border-white/10 text-slate-400 hover:text-white flex items-center justify-center">
                    <i className="fa-solid fa-chevron-left text-xs"></i>
                </button>
                <div className="flex-1 text-center">
                    <div className="text-sm text-white font-semibold capitalize">{fmtDayLabel(date)}</div>
                    <input
                        data-testid="professional-agenda-date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value || todayISO())}
                        className="mt-1 bg-slate-900/60 border border-white/10 rounded-lg px-2 py-1 text-xs text-slate-300 outline-none"
                    />
                </div>
                <button onClick={() => shiftDay(1)} className="h-10 w-10 rounded-xl border border-white/10 text-slate-400 hover:text-white flex items-center justify-center">
                    <i className="fa-solid fa-chevron-right text-xs"></i>
                </button>
            </div>

            {loading ? (
                <div className="text-center text-sm text-slate-500 py-10">Carregando…</div>
            ) : items.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-10 rounded-2xl border border-dashed border-white/10">
                    Nenhum agendamento neste dia.
                </div>
            ) : (
                <div className="space-y-2.5">
                    {items.map((a) => {
                        const st = STATUS[a.status] || STATUS["agendado"];
                        return (
                            <div key={a.id} data-testid={`professional-appt-${a.id}`} className="rounded-2xl border border-white/10 bg-slate-900/40 p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-amber-300 text-sm font-bold">{a.time}</span>
                                            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                                        </div>
                                        <div className="text-white font-semibold mt-1 truncate">{a.client_name}</div>
                                        <div className="text-xs text-slate-400 truncate">{(a.service_names || []).join(", ") || "—"}</div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-emerald-300 font-mono text-sm">{brl(a.total)}</div>
                                        {a.duration_min ? <div className="text-[10px] text-slate-500">{a.duration_min} min</div> : null}
                                    </div>
                                </div>
                                {a.notes ? <div className="text-[11px] text-slate-500 mt-2 border-t border-white/5 pt-2">{a.notes}</div> : null}
                                {(a.status === "agendado" || a.status === "em-andamento") && (
                                    <div className="mt-3 flex gap-2">
                                        {a.status === "agendado" && (
                                            <button
                                                data-testid={`professional-appt-start-${a.id}`}
                                                onClick={() => setStatus(a, "em-andamento")}
                                                disabled={busyId === a.id}
                                                className="flex-1 min-h-[40px] rounded-xl bg-sky-500/90 hover:bg-sky-400 text-slate-950 font-bold text-xs uppercase tracking-wider disabled:opacity-40 flex items-center justify-center gap-2"
                                            >
                                                <i className={`fa-solid ${busyId === a.id ? "fa-spinner fa-spin" : "fa-play"}`}></i>Iniciar
                                            </button>
                                        )}
                                        {a.status === "em-andamento" && (
                                            <button
                                                data-testid={`professional-appt-finish-${a.id}`}
                                                onClick={() => setStatus(a, "concluido")}
                                                disabled={busyId === a.id}
                                                className="flex-1 min-h-[40px] rounded-xl bg-emerald-500/90 hover:bg-emerald-400 text-slate-950 font-bold text-xs uppercase tracking-wider disabled:opacity-40 flex items-center justify-center gap-2"
                                            >
                                                <i className={`fa-solid ${busyId === a.id ? "fa-spinner fa-spin" : "fa-check"}`}></i>Concluir
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── Meu Faturamento ──────────────────────────────────────────────────
const EarningsTab = () => {
    const [period, setPeriod] = useState(monthKey());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const periods = useMemo(() => {
        const out = [];
        const now = new Date();
        for (let i = 0; i < 6; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            out.push({ key: monthKey(d), label: d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }) });
        }
        return out;
    }, []);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        professionalApi.earnings(period)
            .then((r) => { if (alive) setData(r); })
            .catch((e) => { if (alive) { toast.error("Falha ao carregar faturamento", { description: e?.message }); setData(null); } })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [period]);

    const maxDay = useMemo(() => Math.max(1, ...((data?.by_day || []).map((d) => d.amount))), [data]);

    return (
        <div className="space-y-4" data-testid="professional-earnings-tab">
            <select
                data-testid="professional-earnings-period"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-200 outline-none capitalize"
            >
                {periods.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>

            {loading ? (
                <div className="text-center text-sm text-slate-500 py-10">Carregando…</div>
            ) : !data ? (
                <div className="text-center text-sm text-slate-500 py-10">Sem dados.</div>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            ["Comissão total", brl(data.commission_total), "fa-sack-dollar", "text-amber-300"],
                            ["A receber", brl(data.commission_unpaid), "fa-hourglass", "text-amber-300"],
                            ["Já recebido", brl(data.commission_paid), "fa-check", "text-emerald-300"],
                            ["Atendimentos", data.completed_appointments, "fa-scissors", "text-white"],
                        ].map(([l, v, i, cls]) => (
                            <div key={l} className="rounded-xl border border-white/5 bg-slate-900/40 p-4">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 flex items-center gap-1"><i className={`fa-solid ${i}`}></i>{l}</div>
                                <div className={`salon-heading text-xl mt-1 ${cls}`}>{v}</div>
                            </div>
                        ))}
                    </div>

                    <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 mb-3">Comissão por dia</div>
                        {(data.by_day || []).length === 0 ? (
                            <div className="text-xs text-slate-500 text-center py-6">Nenhuma comissão registrada no período.</div>
                        ) : (
                            <div className="space-y-2">
                                {data.by_day.map((d) => (
                                    <div key={d.date} className="flex items-center gap-2 text-xs">
                                        <div className="w-14 text-slate-500 font-mono">{d.date.slice(8)}/{d.date.slice(5, 7)}</div>
                                        <div className="flex-1 h-2 bg-slate-800 rounded overflow-hidden">
                                            <div className="h-full bg-sky-400" style={{ width: `${(d.amount / maxDay) * 100}%` }}></div>
                                        </div>
                                        <div className="w-20 text-right text-sky-300 font-mono">{brl(d.amount)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="text-[11px] text-slate-500 text-center">
                        Comissão {data.commission_type === "fixed" ? "fixa" : `de ${data.commission_value}%`} · {data.entries_count} lançamento(s) no período.
                        A liberação do pagamento é feita pelo salão.
                    </div>
                </>
            )}
        </div>
    );
};

// ─── Shell ────────────────────────────────────────────────────────────
const ProfessionalShell = () => {
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("agenda");

    const loadMe = useCallback(async () => {
        try {
            const m = await professionalApi.me();
            setMe(m);
        } catch (e) {
            // token inválido/expirado ou colaborador desativado → volta ao login
            professionalAuth.clear();
            window.location.href = "/";
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadMe(); }, [loadMe]);

    const logout = () => {
        professionalAuth.clear();
        window.location.href = "/";
    };

    if (loading) {
        return <div className="min-h-screen bg-slate-950 text-slate-500 flex items-center justify-center">Carregando…</div>;
    }
    if (!me) return null;

    if (me.must_change_password) {
        return <ChangePasswordGate onDone={() => { setMe((m) => ({ ...m, must_change_password: false })); loadMe(); }} />;
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="professional-shell">
            <header className="border-b border-white/5 sticky top-0 bg-slate-950/95 backdrop-blur-xl z-20">
                <div className="max-w-lg mx-auto px-5 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 truncate">{me.shop_name || "Kinng"}</div>
                        <h1 className="salon-heading text-lg text-white leading-tight truncate">Olá, {(me.name || "").split(" ")[0]}</h1>
                    </div>
                    <button data-testid="professional-logout" onClick={logout}
                        className="h-9 w-9 rounded-xl border border-white/10 text-slate-400 hover:text-white flex items-center justify-center shrink-0">
                        <i className="fa-solid fa-right-from-bracket text-xs"></i>
                    </button>
                </div>
                <div className="max-w-lg mx-auto px-5 flex gap-1 pb-2">
                    {[["agenda", "Minha Agenda", "fa-calendar-day"], ["earnings", "Meu Faturamento", "fa-chart-line"]].map(([id, label, icon]) => (
                        <button
                            key={id}
                            data-testid={`professional-tab-${id}`}
                            onClick={() => setTab(id)}
                            className={`flex-1 min-h-[40px] rounded-xl text-xs font-semibold uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${
                                tab === id ? "bg-amber-500/15 text-amber-200 border border-amber-500/30" : "text-slate-500 border border-transparent hover:text-slate-300"
                            }`}
                        >
                            <i className={`fa-solid ${icon}`}></i>
                            <span>{label}</span>
                        </button>
                    ))}
                </div>
            </header>

            <main className="max-w-lg mx-auto px-5 py-5">
                {tab === "agenda" ? <AgendaTab /> : <EarningsTab />}
            </main>
        </div>
    );
};

export default ProfessionalShell;
